import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, Dialog, DialogActions, DialogContent, MenuItem, Stack, TextField } from '@mui/material'

import GRDialogTitle from '../../../../../components/GRDialogTitle/GRDialogTitle'
import { Store } from '../../../../../features/store/types/Store'
import { StoreMutation } from '../../../../api/stores'

const storeEcosystems = ['Android', 'iOS', 'Windows']

type StoreFormValues = Pick<Store, 'name' | 'ecosystem'>

type StoreEditDialogProps = {
  open: boolean
  store?: Store
  isSaving?: boolean
  onClose: () => void
  onSave: (store: StoreMutation) => void
}

export const StoreEditDialog: FC<StoreEditDialogProps> = ({ open, store, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<StoreFormValues>({
    mode: 'onChange',
    defaultValues: { name: '', ecosystem: '' },
  })

  useEffect(() => {
    if (open) {
      reset({ name: store?.name ?? '', ecosystem: store?.ecosystem ?? '' })
    }
  }, [open, store, reset])

  const submit = handleSubmit((values) => onSave({ ...store, ...values }))

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit} noValidate>
        <GRDialogTitle onClose={onClose}>{store ? t('internal-service-management:stores_edit') : t('internal-service-management:stores_add')}</GRDialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => <TextField {...field} label={t('internal-common:name')} error={!!fieldState.error} required autoFocus />}
            />
            <Controller
              name="ecosystem"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField {...field} select label={t('internal-service-management:stores_ecosystem')} error={!!fieldState.error} required>
                  {storeEcosystems.map((ecosystem) => (
                    <MenuItem key={ecosystem} value={ecosystem}>
                      {ecosystem}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('internal-common:cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isSaving || !formState.isValid}>
            {t('internal-common:save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
